/**
 * The world's sound around the listener: beds that are always there, louder
 * or softer as the place and the weather change, and calls that come now and
 * then (`core/audio/ambience.ts` says how much of each).
 *
 * - Beds: the wind (a band of noise, gusting, a whistle when it blows hard),
 *   rain light and heavy and on the roof, a town's hum and its chatter, the
 *   sea's swell, crickets; and in the air, the rush of falling and the
 *   canopy's flutter.
 * - Calls: birds in the trees around, gulls overhead, the church bell on the
 *   hour, thunder after each flash, as late as the strike is far.
 */

import type { Beds, Place } from '../../core/audio/ambience.js';
import { bedsAt, bellStrokes, thunderDelay, thunderIsNear, windNow } from '../../core/audio/ambience.js';
import { birdId } from '../../core/audio/bank.js';
import type { Bird } from '../../core/audio/sounds.js';
import type { At, Bed, SoundSystem } from './system.js';

interface BedSet {
  wind: Bed;
  whistle: Bed;
  rainLight: Bed;
  rainHeavy: Bed;
  roof: Bed;
  town: Bed;
  chatter: Bed;
  waves: Bed;
  crickets: Bed;
  air: Bed;
  flutter: Bed;
}

/** What else the listener is doing that the world's sound hears. */
export interface WorldExtras {
  /** In a car's cabin. */
  inside: boolean;
  /** The wind's mean and its peak gusts, m/s. */
  windMean: number;
  windPeak: number;
  /** A lightning flash this frame. */
  flash: boolean;
  /** Local hour, for the bell. */
  clock: number;
  /** Falling through the air, m/s; under a canopy. */
  airspeed: number;
  canopy: boolean;
}

export class WorldSound {
  private beds: BedSet | undefined;
  private gust = 0;
  private gustTarget = 0;
  private gustTimer = 0;
  private nextBird = 4;
  private nextGull = 10;
  private lastClock = -1;
  private wavePhase = 0;
  private flutterPhase = 0;
  /** The town's church, somewhere off to one side. */
  private readonly church = { bearing: Math.random() * Math.PI * 2, distance: 250 + Math.random() * 250 };
  /** The beds at the last update, for the HUD. */
  last: Beds | undefined;

  constructor(private readonly sound: SoundSystem) {}

  private makeBeds(): BedSet | undefined {
    const s = this.sound;
    if (!s.bankReady) return undefined;
    const beds = {
      wind: s.bed('noise:white', 'world', 'bandpass', 400, 0.9),
      whistle: s.bed('noise:white', 'world', 'bandpass', 1200, 14),
      rainLight: s.bed('rain:light', 'world'),
      rainHeavy: s.bed('rain:heavy', 'world'),
      roof: s.bed('rain:roof', 'effects', 'lowpass', 1500),
      town: s.bed('noise:brown', 'world', 'lowpass', 260),
      chatter: s.bed('babble', 'world', 'lowpass', 2400),
      waves: s.bed('noise:brown', 'world', 'lowpass', 500),
      crickets: s.bed('crickets', 'world'),
      air: s.bed('noise:white', 'effects', 'bandpass', 700, 0.6),
      flutter: s.bed('noise:white', 'effects', 'bandpass', 380, 1.1),
    };
    for (const bed of Object.values(beds)) if (bed === undefined) return undefined;
    return beds as BedSet;
  }

  update(dt: number, place: Place, listener: At, x: WorldExtras): void {
    const s = this.sound, ctx = s.context;
    if (ctx === undefined || !s.live) return;
    this.beds ??= this.makeBeds();
    const beds = this.beds;
    if (beds === undefined) return;
    const t = ctx.currentTime;

    // The gust: a slow random walk toward a new target every few seconds.
    this.gustTimer -= dt;
    if (this.gustTimer <= 0) {
      this.gustTarget = Math.random();
      this.gustTimer = 2 + Math.random() * 6;
    }
    this.gust += (this.gustTarget - this.gust) * Math.min(1, dt * 0.8);
    const wind = windNow(x.windMean, x.windPeak, this.gust);
    const b = bedsAt({ ...place, wind }, x.inside);
    this.last = b;

    const level = (bed: Bed, v: number, tau = 0.4) => bed.gain.gain.setTargetAtTime(v, t, tau);
    level(beds.wind, 0.3 * b.wind);
    beds.wind.filter.frequency.setTargetAtTime(b.windPitch, t, 0.5);
    level(beds.whistle, 0.05 * Math.max(0, b.wind - 0.55));
    beds.whistle.filter.frequency.setTargetAtTime(b.windPitch * 3.2, t, 0.8);
    level(beds.rainLight, 0.35 * b.rainLight, 1);
    level(beds.rainHeavy, 0.45 * b.rainHeavy, 1);
    level(beds.roof, 0.5 * b.roof, 0.5);
    level(beds.town, 0.14 * b.town, 1.5);
    level(beds.chatter, 0.1 * b.chatter, 1.5);
    level(beds.crickets, 0.1 * b.crickets, 2);
    // The sea's swell: a wave every eight or nine seconds, its wash brightening as it breaks.
    this.wavePhase += dt / 8.5;
    const swell = 0.5 - 0.5 * Math.cos(this.wavePhase * 2 * Math.PI);
    level(beds.waves, 0.4 * b.waves * (0.35 + 0.65 * swell), 0.6);
    beds.waves.filter.frequency.setTargetAtTime(250 + 900 * swell * swell, t, 0.6);
    // Falling: the air's roar with speed; under the canopy, the fabric flapping.
    const fall = Math.min(1, x.airspeed / 55);
    level(beds.air, x.canopy ? 0.15 * fall : 0.7 * fall * fall, 0.2);
    beds.air.filter.frequency.setTargetAtTime(300 + 900 * fall, t, 0.3);
    this.flutterPhase += dt * 11;
    level(beds.flutter, x.canopy ? 0.35 * (0.5 + 0.5 * Math.sin(this.flutterPhase * 2 * Math.PI)) * Math.min(1, x.airspeed / 6) : 0, 0.01);

    this.calls(dt, b, place, listener, x);
  }

  private calls(dt: number, b: Beds, place: Place, listener: At, x: WorldExtras): void {
    const s = this.sound;
    // Birds: a song from a tree somewhere around, at their rate.
    this.nextBird -= dt;
    if (this.nextBird <= 0) {
      this.nextBird = b.birdsPerMinute > 0.05 ? (-Math.log(1 - Math.random()) * 60) / b.birdsPerMinute : 5;
      if (b.birdsPerMinute > 0.05) {
        const bird = pickBird(place);
        s.play(birdId(bird, Math.floor(Math.random() * 3)), {
          bus: 'world', at: around(listener, 12, 70, 4, 12), gain: 0.35 + 0.3 * Math.random(), rate: 0.95 + 0.1 * Math.random(), near: 10,
        });
      }
    }
    this.nextGull -= dt;
    if (this.nextGull <= 0) {
      this.nextGull = b.gullsPerMinute > 0.05 ? (-Math.log(1 - Math.random()) * 60) / b.gullsPerMinute : 8;
      if (b.gullsPerMinute > 0.05) {
        s.play(`gull:${Math.floor(Math.random() * 3)}`, { bus: 'world', at: around(listener, 30, 140, 15, 40), gain: 0.5, rate: 0.9 + 0.2 * Math.random(), near: 25 });
      }
    }
    // The church bell, on the hour, in town.
    if (this.lastClock >= 0 && place.urban > 0.3) {
      const strokes = bellStrokes(this.lastClock, x.clock);
      const at = { x: listener.x + Math.cos(this.church.bearing) * this.church.distance, y: listener.y + 30, z: listener.z + Math.sin(this.church.bearing) * this.church.distance };
      for (let k = 0; k < strokes; k++) s.play('bell', { bus: 'world', at, gain: 0.9, delay: k * 2.4, near: 120 });
    }
    this.lastClock = x.clock;
    // Thunder after the flash: a strike somewhere within a few kilometres.
    if (x.flash) {
      const distance = 400 + Math.random() * 6500;
      const near = thunderIsNear(distance);
      const bearing = Math.random() * Math.PI * 2;
      s.play(`thunder:${near ? 'near' : 'far'}:${Math.floor(Math.random() * 2)}`, {
        bus: 'world', at: { x: listener.x + Math.cos(bearing) * distance, y: listener.y + 800, z: listener.z + Math.sin(bearing) * distance },
        delay: thunderDelay(distance), gain: 1, rate: 0.85 + 0.3 * Math.random(), near: 900,
      });
    }
  }
}

/** Somewhere `min`..`max` metres around `at`, `lo`..`hi` above it: where a bird sings from. */
function around(at: At, min: number, max: number, lo: number, hi: number): At {
  const a = Math.random() * Math.PI * 2, r = min + (max - min) * Math.random();
  return { x: at.x + Math.cos(a) * r, y: at.y + lo + (hi - lo) * Math.random(), z: at.z + Math.sin(a) * r };
}

/** Which bird: blackbirds and robins in gardens and parks, finches and tits in the woods. */
function pickBird(place: Place): Bird {
  const r = Math.random();
  if (place.urban > 0.4) return r < 0.45 ? 'blackbird' : r < 0.75 ? 'robin' : 'tit';
  return r < 0.4 ? 'finch' : r < 0.65 ? 'tit' : r < 0.85 ? 'blackbird' : 'robin';
}
